import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { deleteAsset } from 'store/assetsSlice';

interface DisplayAsset {
  id: number;
  name: string;
  asset_tag: string;
  serial_number: string;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  // ... other properties
  deviceName: string;
}

interface RemoveAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: DisplayAsset | null;
  onDelete: (deletedAssetId: number) => void;
}

interface ApiError {
  response?: {
    data?: {
      detail?: string;
      message?: string;
    };
  };
  message?: string;
}

const RemoveAssetModal = ({
  show,
  handleClose,
  asset,
  onDelete
}: RemoveAssetModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!asset) return null;

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await dispatch(deleteAsset(asset.id)).unwrap();
      onDelete(asset.id);
      handleClose();
    } catch (err: unknown) {
      const apiError = err as ApiError;
      const errorMessage =
        apiError?.response?.data?.detail ||
        apiError?.response?.data?.message ||
        apiError?.message ||
        'Failed to delete asset. Please try again.';
      setError(errorMessage);
      console.error('Delete asset error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Remove Asset</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Are you sure you want to delete the asset{' '}
          <strong>"{asset.deviceName}"</strong>?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading}>
          {loading ? 'Deleting...' : 'Delete Asset'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveAssetModal;
